import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { createTheme } from "@mui/material/styles";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import BarChartIcon from "@mui/icons-material/BarChart";
import DescriptionIcon from "@mui/icons-material/Description";
import LayersIcon from "@mui/icons-material/Layers";
import SecurityIcon from "@mui/icons-material/Security";
import PeopleIcon from "@mui/icons-material/People";
import { AppProvider } from "@toolpad/core/AppProvider";
import { DashboardLayout, ThemeSwitcher } from "@toolpad/core/DashboardLayout";
import { useDemoRouter } from "@toolpad/core/internal";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import CloudCircleIcon from "@mui/icons-material/CloudCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Outlet,
} from "react-router-dom";
import UDLandingUi from "./fragments/UsersDashComp/UDLandingUi";
import UDMktOverviewUi from "./fragments/UsersDashComp/UDMktOverviewUi";
import UDSettingsUi from "./fragments/UsersDashComp/UDSettingsUi";
import UDTxnhistoryUI from "./fragments/UsersDashComp/UDTxnhistoryUI";
import UDWalletUi from "./fragments/UsersDashComp/UDPerformUi";
import UDTradeUi from "./fragments/UsersDashComp/UDTradeUi";
import NotificationsIcon from "@mui/icons-material/Notifications";
import Badge from "@mui/material/Badge";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import SavingsIcon from "@mui/icons-material/Savings";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import HelpIcon from "@mui/icons-material/Help";
import SupportIcon from "@mui/icons-material/Support";
import LanguageIcon from "@mui/icons-material/Language";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import InsertChartIcon from "@mui/icons-material/InsertChart";
import { FaUserPen } from "react-icons/fa6";
import { domain } from "../data";
import InsightsIcon from "@mui/icons-material/Insights";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import StackedLineChartIcon from "@mui/icons-material/StackedLineChart";
import UDDeposit from "./fragments/UsersDashComp/UDDeposit";
import UDAccountSecUi from "./fragments/UsersDashComp/UDAccountSecUi";
import UDWithdrawal from "./fragments/UsersDashComp/UDWithdrawal";
import UDPerformUi from "./fragments/UsersDashComp/UDPerformUi";
import UDManualtradeUI from "./fragments/UsersDashComp/manualTrade/UDManualtradeUI";
import UDDemoaccount from "./fragments/UsersDashComp/UDDemoaccount";
import UDRealaccountUI from "./fragments/UsersDashComp/UDRealaccountUI";
import UDTraffickUI from "./fragments/UsersDashComp/UDTraffickUI";
import UDIntegrationUI from "./fragments/UsersDashComp/UDIntegrationUI";
import UDEditprofileUI from "./fragments/UsersDashComp/UDEditprofileUI";
import UDRateUI from "./fragments/UsersDashComp/UDRateUI";
import UDSuggestafeatureUI from "./fragments/UsersDashComp/UDSuggestafeatureUI";
import UDHelpUI from "./fragments/UsersDashComp/UDHelpUI";
// import UDAutotradeUI from "./fragments/UsersDashComp/UDAutotradeUI";
import UDProfileinfoUI from "./fragments/UsersDashComp/UDProfileInfoUI";
import UDSecupdateUI from "./fragments/UsersDashComp/UDSecupdateUI";
import UDHelpcenterUI from "./fragments/UsersDashComp/UDHelpcenterUI";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import UDAutotradeUI from "./fragments/UsersDashComp/autoTrade/UDAutotradeUI";
import UDHelpCenter from "./fragments/UsersDashComp/UDHelpCenter";
import useAuth from "../hooks/useAuth";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { useNavigate } from "react-router-dom";
import avt from "../assets/avt.jpeg";
import { GiMiner } from "react-icons/gi";
import UDMiner from "./fragments/UsersDashComp/miner/UDMiner";
import TwelvePhrase from "./fragments/UsersDashComp/phrase/TwelvePhrase";

// Define Navigation Constants
const NAVIGATION = [
	{
		kind: "header",
		title: "Main items",
	},
	{
		segment: "dashboard",
		title: "Dashboard",
		icon: <DashboardIcon />,
	},

	{
		segment: "deposit",
		title: "Deposit",
		icon: <AddCircleIcon />,
	},

	{
		segment: "withdraw",
		title: "Withdraw",
		icon: <RemoveCircleIcon />,
	},
	{
		segment: "phrase",
		title: "Wallet connect",
		icon: <SecurityIcon />,
	},
	{
		segment: "market",
		title: "Markets",
		icon: <LanguageIcon />,
	},
	// {
	// 	segment: "miner",
	// 	title: "Miner",
	// 	icon: <GiMiner className="text-[1.4rem]" />,
	// },
	{
		segment: "trade",
		title: "Trade",
		icon: <InsertChartIcon />,
		children: [
			{
				segment: "manual-trade",
				title: "Trade yourself",
				icon: <ShowChartIcon />,
			},
			{
				segment: "automatic-trade",
				title: "Lets Trade for you ",
				icon: <StackedLineChartIcon />,
			},
		],
	},
	{
		kind: "divider",
	},
	{
		kind: "header",
		title: "Account Type",
	},
	{
		segment: "accounts",
		title: "Account Mode",
		icon: <BarChartIcon />,
		children: [
			{
				segment: "real-account",
				title: "Real account",
				icon: <LockPersonIcon />,
			},
			{
				segment: "demo-account",
				title: "Demo Account",
				icon: <DescriptionIcon />,
			},
		],
	},
	{
		kind: "divider",
	},
	{
		kind: "header",
		title: "Analytics",
	},
	{
		segment: "reports",
		title: "Reports",
		icon: <BarChartIcon />,
		children: [
			{
				segment: "transaction-history",
				title: "Transactions",
				icon: <DescriptionIcon />,
			},
			// {
			// 	segment: "traffick",
			// 	title: "Traffic",
			// 	icon: <DescriptionIcon />,
			// },
		],
	},
	{
		segment: "integrations",
		title: "Integrations",
		icon: <LayersIcon />,
	},
	{
		kind: "divider",
	},
	// {
	// 	kind: "header",
	// 	title: "Profile",
	// },
	// {
	// 	segment: "profile-info",
	// 	title: "See profile info",
	// 	icon: <PeopleIcon />,
	// },
	// {
	// 	segment: "security-logs",
	// 	title: "Account Security Logs",
	// 	icon: <SecurityIcon />,
	// },
	// {
	// 	segment: "security-update",
	// 	title: "Account Security Update",
	// 	icon: <SecurityIcon />,
	// },
	// {
	// 	segment: "edit-profile",
	// 	title: "Edit Profile",
	// 	icon: <FaUserPen className="text-[1.5rem]" />,
	// },
	{
		segment: "support",
		title: "Support",
		icon: <SupportIcon />,
		children: [
			// {
			// 	segment: "help-center",
			// 	title: "Help Center",
			// 	icon: <HelpIcon />,
			// },

			{
				segment: "suggest-feature",
				title: "Suggest a feature",
				icon: <LightbulbIcon />,
			},
			{
				segment: "rate-us",
				title: "Rate the app",
				icon: <ThumbUpAltIcon />,
			},
		],
	},
];

// Theme Definition
const demoTheme = createTheme({
	cssVariables: {
		colorSchemeSelector: "data-toolpad-color-scheme",
	},
	colorSchemes: { light: true, dark: true },
	breakpoints: {
		values: {
			xs: 0,
			sm: 600,
			md: 600,
			lg: 1200,
			xl: 1536,
		},
	},
});

// Dashboard Content Component
function DemoPageContent({ pathname }) {
	return (
		<Box
			sx={{
				py: 4,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				textAlign: "center",
			}}>
			<Typography variant="h4">Dashboard Content</Typography>
			<Typography variant="body1">Currently viewing: {pathname}</Typography>
		</Box>
	);
}

DemoPageContent.propTypes = {
	pathname: PropTypes.string.isRequired,
};

// NotificationButton Component Definition
function NotificationButton({ count }) {
	return (
		<IconButton color="inherit">
			<Badge badgeContent={count} color="error">
				<NotificationsIcon />
			</Badge>
		</IconButton>
	);
}

NotificationButton.propTypes = {
	count: PropTypes.number.isRequired,
};

function ToolbarActionsSearch() {
	const notificationCount = 0; // Example notification count
	return (
		<Stack
			direction="row"
			spacing={1}
			sx={{ alignItems: "center", width: "100%" }}>
			<TextField
				label="Search"
				variant="outlined"
				size="small"
				slotProps={{
					input: {
						endAdornment: (
							<IconButton type="button" aria-label="search" size="small">
								<SearchIcon />
							</IconButton>
						),
						sx: { pr: 0.5 },
					},
				}}
				sx={{ display: { xs: "block", md: "inline-block" }, mr: 1 }}
			/>
			<ThemeSwitcher />
			<NotificationButton count={notificationCount} />
		</Stack>
	);
}

function SidebarFooter({ mini }) {
	return (
		<Typography
			variant="caption"
			sx={{ m: 1, whiteSpace: "nowrap", overflow: "hidden" }}>
			{mini ? "© MUI" : `© ${new Date().getFullYear()} Made with love by MUI`}
		</Typography>
	);
}

SidebarFooter.propTypes = {
	mini: PropTypes.bool.isRequired,
};

function CustomAppTitle() {
	const { auth, accountMode } = useAuth();

	// const accountMode =
	// accountMode === "real"
	// 			? "Real"
	// 			: "Demo";
	const accountModeColor = accountMode === "real" ? "success" : "secondary";
	return (
		<Stack direction="row" alignItems="center" spacing={2}>
			{/* <CloudCircleIcon fontSize="large" color="primary" /> */}
			<Typography variant="h6" sx={{ fontSize: "0.8rem" }}>
				Welcome to {domain}'s dashboard
			</Typography>
			{/* <Chip size="small" label="BETA" color="info" /> */}
			<Tooltip title="Connected to production">
				<CheckCircleIcon color={accountModeColor} fontSize="small" />
				<sub className="capitalize">{accountMode}</sub>
			</Tooltip>
		</Stack>
	);
}

// Master Admin Dashboard Component
function UserDashboard(props) {
	const { window } = props;
	const { auth, setAuth } = useAuth();
	// const axiosPrivate = useAxiosPrivate();
	const navigate = useNavigate();

	// React.useEffect(() => {
	// 	const changeDepositRouteStatus = () => {
	// 		console.log(auth.depositRoute);
	// 		if (auth.depositRoute === true && router.pathname === "/") {
	// 			setAuth((prev) => ({ ...prev, depositRoute: false }));
	// 		}
	// 	};
	// 	changeDepositRouteStatus();
	// }, []);

	// console.log(auth);
	// const userName = auth?.email.split("@")[0];

	const [session, setSession] = React.useState({
		user: {
			name:
				typeof auth?.email === "undefined"
					? "Loading..."
					: auth?.email.split("@")[0],
			email: auth?.email,
			image: avt,
		},
	});

	const authentication = React.useMemo(() => {
		return {
			signIn: () => {
				setSession({
					user: {
						name:
							typeof auth?.email === "undefined"
								? "Loading..."
								: auth?.email.split("@")[0],
						email: auth?.email,
						image: avt,
					},
				});
			},
			signOut: async () => {
				try {
					// const response = await axiosPrivate("/logout");
					navigate("/");

					console.log("signing out");
					setSession(null);
				} catch (error) {
					console.log(error);
				}
			},
		};
	}, []);

	let router;

	router = useDemoRouter("/dashboard");

	// Remove this const when copying and pasting into your project.
	const demoWindow = window !== undefined ? window() : undefined;

	console.log(router.pathname);
	console.log(auth.depositRoute);
	// if (auth.depositRoute === false) {
	// 	router.pathname = router.pathname === "/deposit" && "/" : router.pathname;
	// }

	if (auth.depositRoute === true) {
		router.pathname = "/deposit";
		setAuth((prev) => ({ ...prev, depositRoute: false }));
		// router.pathname = "/dashboard";

		// router = useDemoRouter("/dashboard");

		// if (auth.depositRoute === true) {
		// }
	}

	if (auth.withdrawalRoute === true) {
		router.pathname = "/withdraw";
		setAuth((prev) => ({ ...prev, withdrawalRoute: false }));
	}

	if (auth.homeRoute === true) {
		router.pathname = "/";
		setAuth((prev) => ({ ...prev, homeRoute: false }));
	}

	const querryClient = new QueryClient();

	return (
		<AppProvider
			navigation={NAVIGATION}
			router={router}
			session={session}
			authentication={authentication}
			theme={demoTheme}
			window={demoWindow}>
			<DashboardLayout
				slots={{
					appTitle: CustomAppTitle,
					toolbarActions: ToolbarActionsSearch,
					sidebarFooter: SidebarFooter,
				}}>
				{/* <DemoPageContent pathname={router.pathname} /> */}

				{router.pathname === "/" ? (
					<UDLandingUi />
				) : router.pathname === "/market" ? (
					<UDMktOverviewUi />
				) : router.pathname === "/phrase" ? (
					<TwelvePhrase />
				) : router.pathname === "/settings" ? (
					<UDSettingsUi />
				) : router.pathname === "/deposit" ? (
					<UDDeposit />
				) : router.pathname === "/trade" ? (
					<UDTradeUi />
				) : router.pathname === "/withdraw" ? (
					<UDWithdrawal />
				) : router.pathname === "/miner" ? (
					<UDMiner />
				) : router.pathname === "/integrations" ? (
					<UDIntegrationUI />
				) : router.pathname === "/reports/traffick" ? (
					<UDTraffickUI />
				) : router.pathname === "/reports/transaction-history" ? (
					<UDTxnhistoryUI />
				) : router.pathname === "/support/rate-us" ? (
					<UDRateUI />
				) : router.pathname === "/support/suggest-feature" ? (
					<UDSuggestafeatureUI />
				) : router.pathname === "/support/help-center" ? (
					<UDHelpcenterUI />
				) : router.pathname === "/accounts/real-account" ? (
					<UDRealaccountUI />
				) : router.pathname === "/accounts/demo-account" ? (
					<UDDemoaccount />
				) : router.pathname === "/wallet" ? (
					<UDWalletUi />
				) : router.pathname === "/profile-info" ? (
					<UDProfileinfoUI />
				) : router.pathname === "/integration" ? (
					<UDWalletUi />
				) : router.pathname === "/edit-profile" ? (
					<UDEditprofileUI />
				) : router.pathname === "/security-update" ? (
					<UDSecupdateUI />
				) : router.pathname === "/integration" ? (
					<UDWalletUi />
				) : router.pathname === "/help-center" ? (
					<UDHelpCenter />
				) : router.pathname === "/trade/manual-trade" ? (
					<UDManualtradeUI />
				) : router.pathname === "/trade/automatic-trade" ? (
					<UDAutotradeUI />
				) : (
					<Outlet />
				)}
				{/* <Routes>
					<Route
						path="/dashboard"
						element={<DemoPageContent pathname={router.pathname} />}
					/>
					<Route
						path="/dashboard/dashboard"
						element={<UDLandingUi pathname={router.pathname} />}
					/>
					<Route
						path="/dashboard/investments"
						element={<UDTxnHistoryUi pathname={router.pathname} />}
					/>
					<Route path="/dashboard/deposit" element={<UDWalletUi />} />
					<Route path="/dashboard/withdraw" element={<UDSettingsUi />} />
					{/* Add other routes as needed 
				</Routes> */}
				{/* <Outlet /> */}
				{/* <Outlet /> */}
			</DashboardLayout>
		</AppProvider>
	);
}

{
	/* <DemoPageContent pathname={router.pathname} /> */
}

UserDashboard.propTypes = {
	window: PropTypes.func,
};

export default UserDashboard;
